import { supabase, isSupabaseConfigured } from './supabase';
import { staticData, staticDispatch } from './static-data';
import type {
  Company,
  NowItem,
  WritingTeaser,
  CircuitEvent,
  Post,
  Dispatch,
} from './types';

/** Data access layer.
 *
 * Every read function tries Supabase first, falls back to static-data if:
 *   - the client isn't configured (no env vars), OR
 *   - the query errors, OR
 *   - the query returns an empty array.
 *
 * This lets the site render correctly before the DB exists, during the CMS
 * buildout, and after — flipping the switch is just populating rows.
 */

async function trySupabase<T>(
  fn: () => PromiseLike<{ data: unknown; error: unknown }>,
  fallback: T[]
): Promise<T[]> {
  if (!isSupabaseConfigured || !supabase) return fallback;
  try {
    const { data, error } = await fn();
    if (error || !Array.isArray(data) || data.length === 0) return fallback;
    return data as T[];
  } catch {
    return fallback;
  }
}

export async function getCompanies(): Promise<Company[]> {
  return trySupabase(
    () => supabase!.from('companies').select('*').order('year', { ascending: false }),
    staticData.companies
  );
}

export async function getNow(): Promise<NowItem[]> {
  return trySupabase(
    () => supabase!.from('now_items').select('*').order('sort_order', { ascending: true }),
    staticData.now
  );
}

export async function getWritingTeasers(limit = 5): Promise<WritingTeaser[]> {
  return trySupabase(
    () =>
      supabase!
        .from('posts')
        .select('id, slug, date, title, kicker, minutes, published_at')
        .eq('published', true)
        // Order by the displayed month ("YYYY · MM"); published_at only breaks
        // ties, so a stale publish timestamp can't drop a post out of order.
        .order('date', { ascending: false })
        .order('published_at', { ascending: false })
        .limit(limit),
    staticData.writing.slice(0, limit)
  );
}

export async function getAllPosts(): Promise<WritingTeaser[]> {
  return trySupabase(
    () =>
      supabase!
        .from('posts')
        .select('id, slug, date, title, kicker, minutes, published_at')
        .eq('published', true)
        // Month-first ordering (see getWritingTeasers).
        .order('date', { ascending: false })
        .order('published_at', { ascending: false }),
    staticData.writing
  );
}

export async function getPostBySlug(slug: string): Promise<Post | null> {
  if (!isSupabaseConfigured || !supabase) {
    const match = staticData.writing.find((w) => w.slug === slug);
    return match ? { ...match, body_md: stubBody(match.title) } : null;
  }
  try {
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();
    if (error || !data) {
      const match = staticData.writing.find((w) => w.slug === slug);
      return match ? { ...match, body_md: stubBody(match.title) } : null;
    }
    return data as Post;
  } catch {
    return null;
  }
}

/** Latest published dispatch, or the static fallback. */
export async function getCurrentDispatch(): Promise<Dispatch> {
  if (!isSupabaseConfigured || !supabase) return staticDispatch as Dispatch;
  try {
    const { data, error } = await supabase
      .from('dispatches')
      .select('*')
      .eq('published', true)
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (error || !data) return staticDispatch as Dispatch;
    return data as Dispatch;
  } catch {
    return staticDispatch as Dispatch;
  }
}

/** All published dispatches, newest first (for /dispatches archive). */
export async function getAllDispatches(): Promise<Dispatch[]> {
  if (!isSupabaseConfigured || !supabase) return [staticDispatch as Dispatch];
  try {
    const { data, error } = await supabase
      .from('dispatches')
      .select('id,slug,issue_label,filed_location,read_minutes,published_at')
      .eq('published', true)
      .order('published_at', { ascending: false, nullsFirst: false });
    if (error || !data) return [staticDispatch as Dispatch];
    return data as Dispatch[];
  } catch {
    return [staticDispatch as Dispatch];
  }
}

/** One dispatch by URL slug (for /dispatches/:slug detail page). */
export async function getDispatchBySlug(slug: string): Promise<Dispatch | null> {
  if (!isSupabaseConfigured || !supabase) {
    return slug === '2026-q2' ? (staticDispatch as Dispatch) : null;
  }
  try {
    const { data, error } = await supabase
      .from('dispatches')
      .select('*')
      .eq('slug', slug)
      .eq('published', true)
      .maybeSingle();
    if (error || !data) return null;
    return data as Dispatch;
  } catch {
    return null;
  }
}

export async function getCircuit(): Promise<CircuitEvent[]> {
  return trySupabase(
    () => supabase!.from('circuit_events').select('*').order('when_at', { ascending: true }),
    staticData.circuit
  );
}

function stubBody(title: string): string {
  return [
    `# ${title}`,
    '',
    '_This essay is part of the pre-import library. The real body lives in the legacy blog and will appear here once the import runs._',
    '',
    '— D.',
  ].join('\n');
}
